<template>
  <v-container>
    <v-row justify="center">
      <ViewHeader :title="viewTitle" />
      <v-col cols="12" v-if="canViewMapsRankings">
        <v-card>
          <v-row no-gutters class="visibility-container">
            <v-col v-if="!loading.view" class="pa-8">
              <template v-if="!businessAddresses.length > 0">
                <v-row justify="center" align-content="center" class="no-data">
                  <v-col sm="6" class="text-center">
                    <h3 class="mb-3">
                      {{ $t("message.common.addOwnBusinessAddress") }}
                    </h3>
                    <p>
                      {{
                        $t("message.visibility.summary.noBusinessAddressesInfo")
                      }}
                    </p>
                    <div class="d-flex flex-column mt-8">
                      <ui-button
                        color="primary"
                        icon="icon-google"
                        class="mb-2 mx-auto"
                        style="max-width: 250px; width: 100%;"
                        :to="{ name: 'profile-gmb-accounts' }"
                      >
                        {{ $t("message.common.importFacilities") }}
                      </ui-button>
                    </div>
                  </v-col>
                </v-row>
              </template>

              <template
                v-if="businessAddresses && businessAddresses.length > 0"
              >
                <v-row>
                  <v-col cols="12">
                    <p class="summary__subtitle">
                      {{ $t("message.visibility.summary.map.subtitle") }}
                    </p>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col lg="8">
                    <SelectBar
                      :searchActive="true"
                      :items="businessAddresses"
                      :selectedItem="selectedBusinessAddress"
                      :inputPlaceholder="
                        $t('message.common.chosenBusinessAddress')
                      "
                      :buttonName="$t('message.common.addBusinessAddress')"
                      buttonSlug="add_location"
                      @selected="fetchKeywordsForSelectedBusinessAddress"
                      buttonRouteName="business-addresses"
                    >
                      <SelectBusinessAddress
                        slot="selectedItem"
                        slot-scope="data"
                        :data="data"
                      />
                      <SelectBusinessAddress
                        slot="allItems"
                        slot-scope="data"
                        :data="data"
                      />
                    </SelectBar>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" lg="8">
                    <v-row no-gutters>
                      <v-col>
                        <div class="map__container">
                          <v-overlay v-model="overlay" absolute="absolute">
                            <div class="d-flex justify-center align-center">
                              <v-card
                                height="250"
                                width="350"
                                class="d-flex justify-center align-center flex-column pa-4"
                                light
                              >
                                <v-icon x-large color="primary"
                                  >mdi-database-search</v-icon
                                >
                                <h3 class="mb-3">
                                  {{ $t("message.common.noData") }}
                                </h3>
                                <p class="text-center">
                                  {{
                                    $t(
                                      "message.visibility.summary.dataCollectionForKeywordTryAgainLater"
                                    )
                                  }}
                                </p>
                              </v-card>
                            </div>
                          </v-overlay>
                          <Map
                            v-if="center"
                            :center="center"
                            :coords="coords"
                            defaultStyles="min-height: 500px; height: 100%;"
                          >
                            <template v-slot:squares v-if="squares.length > 0">
                              <MapHeatLayer
                                v-for="square in squares"
                                :key="square.id"
                                :square="square"
                              />
                              <MapVisibilityExtremesLayer
                                :squares="squares"
                              ></MapVisibilityExtremesLayer>
                            </template>
                            <template
                              v-slot:marker
                              v-if="coords.length > 0 && squares.length === 0"
                            >
                              <MapMarker
                                v-for="({ icon }, index) in coords"
                                :key="`icon-${index}`"
                                :icon="icon"
                                :markerType="markerType"
                              />
                            </template>
                          </Map>
                          <SectionLoader :status="loading.map" />
                        </div>
                        <MapLegend />
                        <v-alert color="primary" dense outlined class="mb-0">
                          <span
                            v-html="$t('message.visibility.summary.map.info')"
                            style="font-size: 12px;"
                          ></span>
                        </v-alert>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col cols="12" lg="4" class="p-relative">
                    <template
                      v-if="
                        selectedBusinessAddressKeywords.length > 0 &&
                          selectedKeywordId !== null
                      "
                    >
                      <KeywordList
                        :selectedId.sync="selectedKeywordId"
                        :items="selectedBusinessAddressKeywords"
                        :selectedAdditionalItemId="selectedBusinessAddress.id"
                        @update:selectedId="fetchKeywordData"
                      />
                      <SectionLoader :status="loading.keywordList" />
                    </template>
                    <template v-else>
                      <v-col
                        cols="12"
                        class="d-flex justify-center flex-column align-center fill-height"
                      >
                        <v-icon x-large color="primary"
                          >mdi-database-search</v-icon
                        >
                        <h3 class="mb-3">
                          {{ $t("message.common.noData") }}
                        </h3>
                        <p class="text-center">
                          {{ $t("message.visibility.summary.addKeywords") }}
                        </p>
                        <ui-button
                          color="primary"
                          icon="add"
                          class="mb-0 mt-4"
                          @click="
                            $router.push({ name: 'profile-company-in-web' })
                          "
                        >
                          {{ $t("message.common.addNewTerm") }}
                        </ui-button>
                      </v-col>
                    </template>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    lg="6"
                    offset-lg="3"
                    class="mt-5 text-center"
                  >
                    <p class="mb-4">
                      {{ $t("message.visibility.summary.chart.description") }}
                    </p>
                  </v-col>
                </v-row>

                <v-row no-gutters>
                  <v-col class="mt-8">
                    <div class="p-relative">
                      <template
                        v-if="chartData.length > 0 && chartLabels.length > 0"
                      >
                        <Chart
                          :width="370"
                          :height="500"
                          :data="chartData"
                          :labels="chartLabels"
                          :loading="loading.chart"
                        ></Chart>
                      </template>
                      <template v-else>
                        <v-col
                          cols="12"
                          class="d-flex justify-center fill-height flex-column align-center"
                          style="height: 500px;"
                        >
                          <v-icon x-large color="primary">
                            mdi-database-search
                          </v-icon>
                          <h3 class="mb-3 w-100 text-center">
                            {{ $t("message.common.noData") }}
                          </h3>
                          <p>
                            {{
                              $t(
                                "message.visibility.summary.dataCollectionForKeywordTryAgainLater"
                              )
                            }}
                          </p>
                        </v-col>
                      </template>
                      <ChartPagination
                        @changeMonth="changeMonth"
                        :currentMonth="currentMonth"
                        componentName="visibility_business_addresses"
                      />
                      <SectionLoader :status="loading.chart" />
                    </div>
                  </v-col>
                </v-row>
              </template>
            </v-col>
            <SectionLoader :status="loading.view" />
          </v-row>
        </v-card>
      </v-col>

      <!-- <MapsVisibility
        id="other-maps"
        v-if="businessAddresses.length > 0"
        :businessAddresses="paginationChunks[page - 1] || []"
      >
        <template v-slot:pagination>
          <Pagination
            class="mb-3"
            v-if="paginationChunks.length > 1"
            :page="page"
            :per-page="perPage"
            :total-count="totalChunksCount"
            :showOptions="false"
            @change="handlePaginationChange"
          ></Pagination>
        </template>
      </MapsVisibility> -->
    </v-row>
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader";
import Map from "@/components/ui/Map/Map.vue";
import MapMarker from "@/components/ui/Map/Marker.vue";
import MapHeatLayer from "@/components/ui/Map/Layers/Heat.vue";
import MapVisibilityExtremesLayer from "@/components/ui/Map/Layers/VisibilityExtremes.vue";
import KeywordList from "@/components/Visibility/KeywordList.vue";
import MapLegend from "@/components/ui/Map/Legend/Heat.vue";
import SelectBar from "@/components/Visibility/SelectBar.vue";
import SelectBusinessAddress from "@/components/Visibility/SelectBar/BusinessAddresses.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import Chart from "@/components/Visibility/BusinessAddresses/Chart/chart.js";
import ChartPagination from "@/components/Chart/Pagination.vue";
// import MapsVisibility from "@/components/hoc/Visibility/MapsVisibility.vue";

import businessAddressesService from "@/services/businessAddresses";
import keywordsService from "@/services/keywords";
import serviceHandler from "@/helpers/handlers/service";

import { heatmap } from "@/helpers/mixins/heatmap";
import { primary } from "@/config/marker.js";

import moment from "moment";
import cuid from "cuid";
import { scrollToHash } from "@/helpers/mixins/scroll-to-hash";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

export default {
  name: "VisibilityBusinessAddresses",
  mixins: [heatmap, scrollToHash, permissionsInfo, gtmEngagementMixin],
  components: {
    ViewHeader,
    // MapsVisibility,
    KeywordList,
    Map,
    MapMarker,
    MapHeatLayer,
    MapVisibilityExtremesLayer,
    MapLegend,
    SelectBar,
    SelectBusinessAddress,
    SectionLoader,
    Chart,
    ChartPagination
  },
  data() {
    return {
      viewTitle: this.$t("message.visibility.summary.businessAddressesTitle"),
      paginationChunks: [],
      gmbSearch: "",
      page: 1,
      perPage: 15,
      businessAddresses: [],
      GMBbusinessAddresses: [],
      markerType: primary,
      coords: [],
      overlay: true,
      mapLegend: [
        {
          name: this.$t("message.visibility.summary.map.legend.high"),
          color: "#00ff00",
          id: cuid()
        },
        {
          name: this.$t("message.visibility.summary.map.legend.medium"),
          color: "#ffdd00",
          id: cuid()
        },
        {
          name: this.$t("message.visibility.summary.map.legend.low"),
          color: "#ff0000",
          id: cuid()
        },
        {
          name: this.$t("message.visibility.summary.map.legend.nodata"),
          color: "#636363",
          id: cuid()
        }
      ],
      loading: {
        view: false,
        keywordList: false,
        map: false,
        chart: false
      },
      selectedKeywordId: null,
      selectedBusinessAddress: {},
      selectedBusinessAddressKeywords: [],
      selectedKeywordData: [],
      currentMonth: {},
      chartData: [],
      chartLabels: []
    };
  },
  computed: {
    squares() {
      return this.createSquaresFromHeatPoints(this.selectedKeywordData);
    },
    center() {
      if (
        this.selectedBusinessAddress !== undefined &&
        this.selectedBusinessAddress !== null
      ) {
        return this.coords.find(
          item => item.id === this.selectedBusinessAddress.place_id
        );
      }
      return { icon: [52.2200567, 19.2701527], id: cuid() };
    },
    totalChunksCount() {
      return this.paginationChunks.length * this.perPage;
    }
  },

  async mounted() {
    this.loading.view = true;

    this.getCurrentMonth();
    await this.fetchBusinessAddresses();

    if (this.businessAddresses.length > 0) {
      const selectBusinessAddressPromise = this.selectBusinessAddress();
      const fetchKeywordsPromise = this.fetchKeywords();

      await Promise.all([selectBusinessAddressPromise, fetchKeywordsPromise]);

      if (this.selectedBusinessAddressKeywords.length > 0) {
        await this.fetchKeywordData();
      }
    }

    this.loading.view = false;

    this.scrollToHash();
    this.mixin__setGtmEngagementTimeouts();
  },

  methods: {
    async fetchBusinessAddresses() {
      const data = await serviceHandler(
        businessAddressesService.getBusinessAddressesData()
      );

      if (data.length > 0) {
        this.businessAddresses = data;
        this.GMBbusinessAddresses = data;
      }
    },

    getCurrentMonth(month) {
      month = month || moment();
      this.currentMonth = month;
    },

    async getCoords() {
      this.coords = [
        {
          icon: this.selectedBusinessAddress.geo_coords,
          id: this.selectedBusinessAddress.place_id
        }
      ];
    },

    selectBusinessAddress(id) {
      if (!id) {
        this.selectedBusinessAddress = this.businessAddresses[0];
        return;
      }

      this.selectedBusinessAddress = this.businessAddresses.find(
        businessAddress => businessAddress.id === id
      );

      this.getCoords();
    },

    async fetchKeywordsForSelectedBusinessAddress(selectedBusinessAddress) {
      await this.fetchKeywords(selectedBusinessAddress);
      if (this.selectedBusinessAddressKeywords.length > 0) {
        await this.fetchKeywordData();
      }
    },
    async fetchKeywords(selectedBusinessAddress) {
      this.loading.keywordList = true;
      this.loading.chart = true;
      const businessAddressId =
        (selectedBusinessAddress && selectedBusinessAddress.id) ||
        this.selectedBusinessAddress.id;
      const facility_id =
        (selectedBusinessAddress && selectedBusinessAddress.facility_id) ||
        this.selectedBusinessAddress.facility_id;

      this.selectBusinessAddress(businessAddressId);

      const result = await serviceHandler(
        keywordsService.getCompanyKeywords({ facility_id })
      );

      if (result) {
        this.selectedBusinessAddressKeywords = result.map(
          ({ keyword }) => keyword
        );
      }

      this.loading.chart = false;
      this.selectedKeywordId =
        this.selectedBusinessAddressKeywords &&
        this.selectedBusinessAddressKeywords.length > 0
          ? this.selectedBusinessAddressKeywords[0].id
          : null;
      this.loading.keywordList = false;
    },

    async fetchKeywordData(keywordId) {
      this.loading.keywordList = true;
      this.loading.chart = true;
      this.loading.map = true;

      keywordId = !keywordId
        ? this.selectedBusinessAddressKeywords[0].id
        : keywordId;

      const businessAddressId = this.selectedBusinessAddress.id;

      this.getChartData().finally(() => {
        this.loading.chart = false;

        if (!this.loading.map) {
          this.loading.keywordList = false;
        }
      });

      let data = [];
      if (this.canViewMapsRankings) {
        data = await serviceHandler(
          businessAddressesService.getKeywordData({
            businessAddressId,
            keywordId: keywordId
          })
        ).finally(() => {
          this.loading.map = false;

          if (!this.loading.chart) {
            this.loading.keywordList = false;
          }
        });
      } else {
        this.loading.map = false;

        if (!this.loading.chart) {
          this.loading.keywordList = false;
        }
      }
      data.length > 0 ? (this.overlay = false) : (this.overlay = true);
      this.selectedKeywordData = data;

      if (!this.loading.chart && !this.loading.map) {
        this.loading.keywordList = false;
      }
    },

    async getChartData() {
      return new Promise(async (resolve, reject) => {
        const data = [];
        const labels = [];
        const createPositions = arr => {
          const positions = [];
          let dateValue = "";
          arr.forEach(item => {
            if (!positions.length > 0) {
              dateValue = item.searched_at;
              return positions.push(item);
            }
            if (dateValue !== item.searched_at) {
              dateValue = item.searched_at;
              return positions.push(item);
            }
          });

          return positions;
        };

        if (this.canViewMapsRankings) {
          const response = await serviceHandler(
            businessAddressesService.getAvgMapSearchResult(
              {
                address: this.selectedBusinessAddress.id,
                keyword: this.selectedKeywordId
              },
              {
                from: moment(this.currentMonth)
                  .startOf("month")
                  .format("YYYY-MM-DD"),
                to: moment(this.currentMonth)
                  .endOf("month")
                  .startOf("day")
                  .format("YYYY-MM-DD")
              }
            )
          );

          const results = createPositions(response.address[0].results);

          if (results && results.length > 0) {
            results.forEach(item => {
              data.push(item.position_organic);
              labels.push(item.searched_at);
            });
          }
        }

        this.chartData = data.reverse();
        this.chartLabels = labels.reverse();
        resolve();
      });
    },

    async changeMonth(val) {
      this.loading.chart = true;
      this.getCurrentMonth(val);
      await this.getChartData();
      this.loading.chart = false;
    },

    handlePaginationChange({ page, perPage }) {
      this.page = page;
    }
  }
};
</script>

<style lang="scss" scoped>
.visibility-container {
  min-height: 800px;
  position: relative;
}

.map__container {
  position: relative;
  border-radius: 3px;
  overflow: hidden;
}

.summary__subtitle {
  text-align: center;
  font-weight: $font-weight-semi-bold;
  margin-bottom: 0;
}

.no-data {
  height: 100%;
}
.p-relative {
  position: relative;
}
</style>
